import {
  ProjectMemberAttributes,
  ProjectMemberRole,
  ProjectMemberRoleAttributes,
} from '@graphql/generated';
import {
  dedupeProjectMemberInvites,
  dedupeProjectMembers,
} from '@utils/projects';

/**
 * `users` mixes three shapes from Formik's `values.users` — narrowed with
 * type guards below rather than `any` (rule 19 requires `unknown` +
 * narrowing over `any` for a genuinely dynamic shape):
 * - an ordinary selected/contact member (`{ id, role }`, any __typename
 *   other than 'InvitedUser')
 * - a non-contact invite (`{ __typename: 'InvitedUser', email, role }`)
 * - a *whole contact Group* selected as a single member on mobile
 *   (`{ type: 'GROUP', id: <groupId>, role }` — the ContactListMobile
 *   flow, distinct from the web invite modal's per-user flattening)
 */
type OrdinaryMemberEntry = {
  id?: string | null;
  email?: string | null;
  role: ProjectMemberRole;
  __typename?: string;
};

type GroupSelectionEntry = {
  type: 'GROUP';
  id: string;
  role: ProjectMemberRole;
};

type InvitedUserEntry = {
  __typename: 'InvitedUser';
  email: string;
  role: ProjectMemberRole;
};

type ProjectMemberFormEntry =
  | OrdinaryMemberEntry
  | GroupSelectionEntry
  | InvitedUserEntry;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Single narrowing point from Formik's untyped `values.users`: accept
// anything shaped like an object with a role, then let the discriminated
// union guards below do the rest. Anything else (null/undefined/non-object
// junk) is dropped rather than risking a downstream crash.
const toMemberFormEntry = (value: unknown): ProjectMemberFormEntry | null =>
  isRecord(value) && 'role' in value ? (value as ProjectMemberFormEntry) : null;

const isInvitedUserEntry = (
  entry: ProjectMemberFormEntry
): entry is InvitedUserEntry =>
  (entry as InvitedUserEntry).__typename === 'InvitedUser';

const isNotInvitedUserEntry = (
  entry: ProjectMemberFormEntry
): entry is OrdinaryMemberEntry | GroupSelectionEntry =>
  !isInvitedUserEntry(entry);

const isGroupSelectionEntry = (
  entry: OrdinaryMemberEntry | GroupSelectionEntry
): entry is GroupSelectionEntry =>
  (entry as GroupSelectionEntry).type === 'GROUP';

/**
 * The `members`/`allProjectMembers`/`invitedUserMembers` assembly out of
 * ProjectFormStack.submit(), extracted so the wiring itself — not just the
 * underlying dedupeProjectMembers/dedupeProjectMemberInvites helpers in
 * isolation — has a test that would fail if a future edit stopped calling
 * them (rule 24: a wiring regression could otherwise leave this un-deduped
 * while all the pure-helper tests still pass, since none of them drive
 * this actual assembly point).
 *
 * `members` and `allProjectMembers` are two independent representations of
 * "who should be on this project" — one keyed by userId, the other by
 * email — and both reach the same createProject/updateProject mutation
 * call. ProjectForm.web.tsx's
 * onAddEmailInvitations writes the *entire* invitations list (including
 * contacts that resolved to a real user) into `values.projectMembers`,
 * while that same resolved contact also lands in `values.users` as a full
 * member. Deduping each array on its own therefore isn't enough: a
 * contact selected via the invite modal's email search can appear once by
 * userId in `members` and again by email in `allProjectMembers`, and if
 * the backend resolves that email back to the same user, both
 * representations attempt to create the same `project_users` row. Any
 * email already represented as a full member is treated as authoritative
 * and excluded from the email-keyed side.
 */
type EditModeExistingMember = {
  email?: string | null;
  role: ProjectMemberRole;
} & Record<string, unknown>;

/**
 * ProjectForm.web.tsx's onAddEmailInvitations, EDIT-project branch: matches
 * each invitation's email against the project's current members to decide
 * whether it updates an existing member's role or becomes a brand-new
 * entry. Extracted for testability (rule 24 -- same rationale as
 * buildProjectMemberPayload above).
 *
 * All matching is done on lowercased email: an existing member's stored
 * email and an invitation's email can differ only in case (e.g.
 * `Alice@Example.com` vs
 * `alice@example.com`). Without normalizing, the existing member is never
 * marked "processed" and a second entry is appended for the same person
 * with the new role -- while the first, un-updated entry keeps the stale
 * one, producing a visible duplicate with two different roles.
 */
export const resolveEditModeMembers = (
  users: EditModeExistingMember[],
  invitations: ProjectMemberRoleAttributes[],
  userDataByEmail: Map<string, Record<string, unknown>>
): EditModeExistingMember[] => {
  const invitationRoleMap = new Map(
    invitations.map((inv) => [inv.email.toLowerCase(), inv.role])
  );

  const processedEmails = new Set<string>();

  const updatedExistingMembers = users.map((u) => {
    const userEmail = (u.email || '').toLowerCase();
    const matchedRole = userEmail
      ? invitationRoleMap.get(userEmail)
      : undefined;
    if (userEmail && matchedRole !== undefined) {
      processedEmails.add(userEmail);
      return { ...u, role: matchedRole };
    }
    return u;
  });

  const newInvitedMembers = invitations
    .filter((inv) => !processedEmails.has(inv.email.toLowerCase()))
    .map((inv, index) => {
      const fullUserData = userDataByEmail.get(inv.email.toLowerCase());
      if (fullUserData) {
        return {
          id: fullUserData.id || `invited-${index}-${inv.email}`,
          name: fullUserData.name || inv.email,
          firstName: fullUserData.firstName,
          lastName: fullUserData.lastName,
          email: inv.email,
          avatar: fullUserData.avatar,
          role: inv.role,
          __typename: fullUserData.__typename || 'User',
        } as EditModeExistingMember;
      }
      return {
        id: `invited-${index}-${inv.email}`,
        name: inv.email,
        email: inv.email,
        role: inv.role,
        __typename: 'InvitedUser',
      } as EditModeExistingMember;
    });

  return [...updatedExistingMembers, ...newInvitedMembers];
};

export const buildProjectMemberPayload = (
  users: unknown[] | null | undefined,
  projectMembers: ProjectMemberRoleAttributes[] | null | undefined,
  meId: string | null | undefined
): {
  members: ProjectMemberAttributes[];
  invitedUserMembers: ProjectMemberRoleAttributes[];
  allProjectMembers: ProjectMemberRoleAttributes[];
} => {
  const entries = (users || [])
    .map(toMemberFormEntry)
    .filter((entry): entry is ProjectMemberFormEntry => entry !== null);

  const nonInvitedEntries = entries.filter(isNotInvitedUserEntry); // Filter out invited users (they're handled via projectMembers)

  const members: ProjectMemberAttributes[] = dedupeProjectMembers(
    nonInvitedEntries
      .map((entry) => {
        if (isGroupSelectionEntry(entry)) {
          return { userId: meId || '', groupId: entry.id, role: entry.role };
        }
        return {
          userId: entry.id || '',
          groupId: '',
          role: entry.role,
        };
      })
      .filter((m) => m.userId !== '')
  );

  // Emails already represented as a full member above — these must not
  // also reach the mutation via the email-keyed side (see doc comment).
  const memberEmails = new Set(
    nonInvitedEntries
      .filter(
        (entry): entry is OrdinaryMemberEntry => !isGroupSelectionEntry(entry)
      )
      .map((entry) => entry.email)
      .filter((email): email is string => !!email)
      .map((email) => email.toLowerCase())
  );

  // Convert InvitedUser entries (non-contacts) to projectMembers so the
  // backend routes them through invite_members! (pending invitation).
  const existingEmails = new Set(
    (projectMembers || []).map((pm) => pm.email.toLowerCase())
  );
  const invitedUserMembers: ProjectMemberRoleAttributes[] =
    dedupeProjectMemberInvites(
      entries
        .filter(isInvitedUserEntry)
        // Mobile's invite screen can tag a selected user as InvitedUser
        // with a missing optional email, stored as ''. Without this
        // guard, an invalid blank-address entry reaches the mutation --
        // restores the !!u.email guard the previous assembly had.
        .filter((entry) => !!entry.email)
        .filter(
          (entry) =>
            !existingEmails.has(entry.email.toLowerCase()) &&
            !memberEmails.has(entry.email.toLowerCase())
        )
        .map((entry) => ({ email: entry.email, role: entry.role }))
    );

  const allProjectMembers: ProjectMemberRoleAttributes[] =
    dedupeProjectMemberInvites(
      [...(projectMembers || []), ...invitedUserMembers].filter(
        (pm) => !memberEmails.has(pm.email.toLowerCase())
      )
    );

  return { members, invitedUserMembers, allProjectMembers };
};
